import type { RoleId } from '@onuw/roles-data'
import type { RoleGuide } from './types'

/** The Bonus roles, and the table's one custom role (the Giant Wolf). */
export const BONUS_GUIDES = {
  appTanner: {
    lead: 'Knows who the Tanner is, and wins if the Tanner dies.',
    does: ['The Apprentice Tanner wakes and sees which player holds the [[tanner]] card.'],
    rules: [
      'He wins when the Tanner dies, whether or not he survives himself.',
      'With no Tanner among the players at the end of the night, the Apprentice Tanner is the Tanner: he wins only by dying.',
      'He is on the Tanner’s side, and wins with neither the village nor the wolves. The [[revealer]] turns his card back down, as she does the Tanner’s.',
    ],
    tips: [
      'Help the Tanner look guilty without overdoing it: accuse him with a detail that sounds like something you saw.',
      'If you woke and saw nobody, you are the Tanner now. Play as one.',
    ],
    against: [
      'Two players pushing the same suspect may be a wolf hunt, or an Apprentice Tanner at work. Check the story they tell, not how sure they sound.',
    ],
  },
  thing: {
    lead: 'Taps one of the players sitting next to it. That is all, and it is enough to prove who the Thing is.',
    does: [
      'The Thing may tap the player on its left or the player on its right. The tapped player is told only "You’ve been tapped".',
    ],
    rules: [
      'The tapped player does not learn who tapped them, or from which side.',
      'Neighbours are the players beside you at the table, in the seat order the host arranged in the lobby.',
      'The Thing looks at no card and moves none.',
    ],
    tips: [
      'In the morning, wait for your neighbour to say they were tapped, then confirm it. A tap is a fact two players share, and a liar can supply only one half.',
    ],
    against: [
      'A tapped player knows one of their two neighbours is the Thing. If both deny it, one of them is hiding something.',
    ],
  },
  nostradamus: {
    lead: 'Looks at up to three players’ cards and joins the team of the last one he saw, which the table then hears.',
    does: [
      'Nostradamus may look at up to three players’ cards, one at a time. The first card that is not a village card stops him, and he joins that card’s team. Then everyone is told which team Nostradamus is on.',
    ],
    rules: [
      'If every card he saw was a village card, or he looked at none, he stays with the village, and that is what is announced.',
      'He joins the team without becoming the role. A Nostradamus on the Werewolves’ team wins with them but is not a werewolf: killing him does not win the game for the village.',
      'The announcement is made even when his card lies in the center, so it does not prove a Nostradamus is at the table.',
    ],
    tips: [
      'Everyone hears your team and nobody hears your name. With the village, claim openly: the announcement backs you.',
      'On an evil team you know one of them and they do not know you. Play like a [[minion]].',
    ],
    against: [
      '"Nostradamus is on the Werewolves team" tells the village there is one more enemy who is not a wolf card. Voting him out wins nothing.',
    ],
  },
  auraSeer: {
    lead: 'Learns which players looked at a card or moved one during the night.',
    does: [
      'The Aura Seer wakes after the [[troublemaker]] and sees every player who has viewed or moved a card so far.',
    ],
    rules: [
      'Players who only saw other players are not listed: Werewolves in a pack, [[mason|Masons]], the [[minion]]. A lone wolf who looked at the center is.',
      'Roles that wake after her are not listed either: the [[gremlin]], the [[drunk]], the [[insomniac]], the [[revealer]].',
      'She learns who acted, not what they did.',
    ],
    tips: [
      'Set the list against the claims. A self-declared Villager on your list has lied, and a Seer who is not on it either skipped the look or is not the Seer.',
    ],
    against: [
      'With the Aura Seer in the deck, a wolf’s made-up Robber or Seer story can be refuted by one player. Claim a role that touches no card.',
    ],
  },
  beholder: {
    lead: 'At the end of the night, sees who holds the Seer cards.',
    does: [
      'The Beholder wakes near the end of the night and is shown which players hold a [[seer]] or an [[appSeer]] card at that moment.',
    ],
    rules: [
      'She sees where the cards are now, after every swap, not who was dealt them.',
      'With no Seer card in front of a player, she is told there are no seers.',
    ],
    tips: [
      'You can confirm the real Seer, or tell the table that the player dealt the Seer no longer holds it.',
      'Let the Seer claims come first. You are the one who can sort them.',
    ],
    against: [
      'A wolf who claims Seer in a game with a Beholder is taking a real risk. Claiming Beholder to back a fake Seer is the counter, and takes two liars.',
    ],
  },
  squire: {
    lead: 'On the Werewolves’ side: at the end of the night, sees who holds the wolf cards.',
    does: [
      'The Squire wakes near the end of the night and is shown which players hold a werewolf card at that moment.',
    ],
    rules: [
      'He wins with the Werewolves, like the [[minion]], and is not a werewolf himself.',
      'He sees the final layout: a wolf card that was robbed or swapped shows in front of its new player.',
      'With no werewolf among the players, the Squire wins if another player dies and he survives.',
    ],
    tips: [
      'You know better than the wolves who the wolves are now. Steer the vote away from the players who actually hold the cards.',
    ],
    against: [
      'A player who defends someone the wolves themselves seem unsure about may be the Squire, who saw where the card ended up.',
    ],
  },
  prince: {
    lead: 'Cannot be killed by the vote.',
    does: ['The Prince has no night action.'],
    rules: [
      'Votes for the Prince are counted, but he does not die. If he has the most, the player with the next most dies instead, provided they have at least two votes.',
      'The [[hunter]]’s shot does not kill him either. A Mark of Love does.',
      'The power belongs to the card held when the night ends, and to the player with the [[curator]]’s Cloak of the Prince.',
    ],
    tips: [
      'A Prince claim that is believed takes you out of the vote. That is exactly why wolves make it, so back yours with something.',
    ],
    against: [
      'Votes on a real Prince are wasted. If the claim cannot be checked, compare it with the deck before spending the vote there.',
    ],
  },
  cursed: {
    lead: 'A villager who becomes a Werewolf if a werewolf votes for them.',
    does: ['The Cursed has no night action. The change happens in the vote.'],
    rules: [
      'If any werewolf votes for the Cursed, the Cursed is a Werewolf when the votes are counted: their death then counts as a wolf’s death, and they win with the wolves.',
      'If no werewolf votes for them, the Cursed is a plain villager.',
      'The Mark of the Vampire is applied after the curse: a Cursed player who has it is a Vampire.',
    ],
    tips: [
      'You cannot know your team until the votes are in. Vote for a wolf, and hope no wolf votes for you.',
    ],
    against: [
      'A wolf’s vote for the Cursed makes one more wolf the village can kill. Wolves should vote elsewhere.',
    ],
  },
  bodySnatcher: {
    lead: 'An Alien who swaps his card as the night instructs. The card he takes becomes an Alien card for good.',
    does: [
      'The Body Snatcher wakes with the Aliens. Later he wakes alone, and the app announces an instruction to the whole table: swap with any player, a neighbour, an even or odd numbered player, a player with a higher or lower number, or a center card. He may swap his card as told, and looks at the card he took.',
    ],
    rules: [
      'The card he took is an Alien card from then on, whoever holds it when the night ends: the village may win by killing that player.',
      'His own card stays an Alien card too, so the player he gave it to is an Alien as well.',
      'One instruction tells him to do nothing and act as if he had.',
      'If his own card is shielded, he cannot swap.',
    ],
    tips: [
      'The table heard your instruction, so it knows which players you could reach. Claim a role that fits a player outside that group.',
      'The player you swapped with holds your Alien card and does not know. They will argue honestly for a card they no longer have.',
    ],
    against: [
      'After a Body Snatcher swap there are two Alien cards where there was one. Work out from the announced instruction who could have been snatched.',
    ],
  },
  empath: {
    lead: 'Everyone points at a player with their eyes closed. Only the Empath sees who pointed at whom.',
    does: [
      'Near the end of the night the app asks everyone a question (who is the most suspicious, who will win, who you think the Empath is) and every player points at someone, eyes closed. Then the Empath opens her eyes and sees all of it.',
    ],
    rules: [
      'Everyone points, the Empath included, whether or not her card lies in the center.',
      'With the [[doppelganger]] in the deck everyone points a second time, at another question, in case she copied the Empath.',
      'Pointing changes no card and no team.',
    ],
    tips: [
      'What people point at with their eyes closed is honest in a way the day is not. A player who pointed at "most suspicious" and defends that same player in the morning is worth asking about.',
    ],
    against: ['Somebody may be watching where you point. A wolf should point where a villager would.'],
  },
  giantWolf: {
    lead: 'A Werewolf whose vote counts twice.',
    does: [
      'The Giant Wolf wakes with the Werewolves and sees the pack. As a lone wolf he may look at one center card.',
    ],
    rules: [
      'His vote counts as two when the votes are tallied.',
      'He is a werewolf card in every other way: if he dies, the Werewolves lose.',
      'The Giant Wolf is a custom role of this table, not a card from the published game.',
    ],
    tips: [
      'Two votes from one player can make a second vote level out of nothing. With your partner, three votes land on a villager from two players.',
    ],
    against: [
      'With the Giant Wolf in the deck, count the votes in the results: a player who received two votes from a single voter was voted for by the Giant Wolf.',
    ],
  },
} satisfies Partial<Record<RoleId, RoleGuide>>
